import { computed, defineComponent, onMounted, ref, watch, PropType } from 'vue';
import { useStore } from '@/plugins/vuex';
import { useI18N } from '@/plugins/i18n';
import vuetify from '@/plugins/vuetify';
import Filters, { FilterFields } from '@/sections/shared/explore/model/Filters';
import SSelectorChips from '@/components/stemdo-components/s-selector-multiple/SSelectorChips.vue';

export interface ISelectState {
  id?: string;
  desc?: string;
}

export interface IAtocompleteMultipleState {
  id: string;
  desc: string;
}

export default defineComponent({
  components: {
    SSelectorChips
  },
  props: {
    value: {
      type: Array as PropType<string[]>,
      required: false
    },
    items: {
      type: Array as PropType<ISelectState[]>,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    action: {
      type: Function,
      required: true
    },
    inputHeight: {
      type: Number,
      required: false
    },
    filterField: String,
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    labelDisplaySelection: {
      type: Boolean,
      default: false
    },
    label: {
      type: String
    },
    tempFilters: {
      type: Boolean,
      default: false,
      required: false
    },
    mode: {
      type: String as PropType<'default' | 'floatingLabel' | 'chip'>,
      default: 'default'
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const i18n = useI18N();
    const selectedItems = ref([]);
    const labelDisplay = ref('');
    const searchInput = ref('');
    const isInputFocused = ref(false);
    const customStyle = computed(() => {
      const themeColors: any = vuetify.framework.theme.currentTheme;
      return props.tempFilters === true ? { '--custom-color': themeColors.text.lighten2 } : { '--custom-color': themeColors.text.lighten1 };
    });

    onMounted(() => {
      if (props.mode === 'chip') {
        if (props.value && Array.isArray(props.value)) {
          selectedItems.value = props.items.filter(i => props.value.includes(i.id));
        }
      } else if (props.filterField) {
        selectedItems.value = getComponentState();
      }
      setLabelDisplay();
    });

    watch(
      () => props.value,
      value => {
        if (props.mode === 'chip') {
          selectedItems.value = props.items.filter(i => value && value.includes(i.id));
        }
      }
    );

    watch(
      () => props.items,
      value => {
        if (props.mode === 'chip') {
          selectedItems.value = value.filter(i => props.value && props.value.includes(i.id));
        }
      }
    );

    if (!props.tempFilters) {
      watch(
        () => store.getters['filterStore/filters'],
        (filters: Filters) => {
          selectedItems.value = getComponentState();
          setLabelDisplay();
        },
        { deep: true }
      );
    }

    if (props.tempFilters) {
      watch(
        () => store.getters['filterStore/tempFilters'],
        (filters: Filters) => {
          selectedItems.value = getComponentState();
          setLabelDisplay();
        },
        { deep: true }
      );
    }

    watch(selectedItems, () => {
      setLabelDisplay();
    });

    const getComponentState = () => {
      const payload = props.filterField as FilterFields;
      if (!props.tempFilters) {
        return store.getters['filterStore/getAutoCompleteMultipleState'](payload);
      }
      return store.getters['filterStore/getAutoCompleteMultipleTempState'](payload);
    };

    const handleChange = (newSelectedItems: any[]) => {
      if (props.mode === 'chip') {
        emit(
          'input',
          newSelectedItems.map(i => i.id)
        );
        props.action && props.action(props.filterField, newSelectedItems);
      } else {
        const pNewSelectedItems = JSON.parse(JSON.stringify(newSelectedItems));
        props.action && props.action(props.filterField, pNewSelectedItems);
      }
    };

    const isItemSelected = item => {
      if (props.mode === 'chip') {
        return selectedItems.value.find(i => (i.id ?? i) === (item.id ?? item));
      }
      return selectedItems.value.find(i => i.id === item.id);
    };

    const handleBlur = () => {
      setLabelDisplay();
      isInputFocused.value = false;
    };

    const setLabelDisplay = () => {
      if (!props.labelDisplaySelection) {
        labelDisplay.value = i18n.t(props.placeholder).toString();
        return;
      }
      // Always show summary label, even for one selection
      labelDisplay.value = `${i18n.t(props.label).toString()} · ${selectedItems.value.length}`;
    };

    const handleFocus = () => {
      labelDisplay.value = '';
      isInputFocused.value = true;
    };

    function onUpdateSearchInput(val: string) {
      searchInput.value = val;
    }
    function clearSearchInput() {
      searchInput.value = '';
    }
    function existsTech(name: string) {
      const lower = name.trim().toLowerCase();
      const inItems = props.items.some(i => i.desc.trim().toLowerCase() === lower);
      const inSelected = selectedItems.value.some(i => i.desc.trim().toLowerCase() === lower);
      return inItems || inSelected;
    }

    return {
      customStyle,
      selectedItems,
      labelDisplay,
      searchInput,
      isInputFocused,
      handleChange,
      isItemSelected,
      handleFocus,
      handleBlur,
      onUpdateSearchInput,
      clearSearchInput,
      existsTech
    };
  }
});
